import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, X, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Feb-2026 — Globaler „Aktion erforderlich"-Banner.
 *
 * Fetched die Smart-Inbox und zeigt eine dünne, dismissbare Leiste
 * direkt unter dem Header, wenn kritische Items existieren. Damit muss
 * der User nicht ins Dashboard, um Wichtiges zu sehen.
 *
 * Sichtbarkeit:
 *  • Nur eingeloggte User
 *  • Nur auf Seiten außer Dashboard selbst (dort gibt's die Inbox)
 *  • Nur wenn Items mit severity=critical existieren
 *  • Dismiss merkt sich im sessionStorage bis Reload
 */
const SESSION_KEY = "tw.action_banner.dismissed_until";

export default function GlobalActionBanner() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [dismissed, setDismissed] = useState(false);
    const loc = useLocation();

    // Feb-2026 (User-Wunsch) — Smart-Inbox ist für Händler (B2B) standardmäßig
    // deaktiviert; dann auch keinen globalen Aktions-Banner anzeigen.
    const isDealer = user?.role === "b2b";
    const inboxEnabled = true;  // Jun-2026 (User-Wunsch): Smart-Inbox immer aktiv.

    // Fetch Inbox-Items im 90-Sekunden-Takt, aber nicht außerhalb dieser App
    useEffect(() => {
        if (!inboxEnabled) { setItems([]); return; }
        let cancelled = false;
        const run = async () => {
            try {
                const r = await api.get("/dashboard/inbox");
                if (!cancelled) {
                    const crit = (r.data?.items || []).filter((i) => i.severity === "critical");
                    setItems(crit);
                }
            } catch { /* silent: e.g. 401 */ }
        };
        run();
        const t = setInterval(run, 90000);
        return () => { cancelled = true; clearInterval(t); };
    }, [inboxEnabled]);

    // Auto-dismiss bis nächstes Hard-Reload via sessionStorage
    useEffect(() => {
        try {
            const v = sessionStorage.getItem(SESSION_KEY);
            if (v && parseInt(v, 10) > Date.now()) setDismissed(true);
        } catch { /* ignore */ }
    }, []);

    // Auf Dashboard selbst kein Banner — der Inbox-Tab erledigt das
    if (loc.pathname.startsWith("/dashboard")) return null;
    if (!inboxEnabled) return null;
    if (dismissed || items.length === 0) return null;

    // Wenn mehrere critical → fasse zusammen
    const first = items[0];
    const moreCount = items.length - 1;

    const onDismiss = () => {
        setDismissed(true);
        try {
            sessionStorage.setItem(SESSION_KEY, String(Date.now() + 4 * 3600 * 1000));
        } catch { /* ignore */ }
    };

    return (
        <div className="hidden md:block bg-red-50 border-y-2 border-red-300 text-red-900" data-testid="global-action-banner">
            <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3">
                <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                <div className="flex-1 min-w-0 text-sm">
                    <span className="font-bold">{first.title}</span>
                    {first.body && <span className="hidden sm:inline text-red-800/80"> · {first.body}</span>}
                    {moreCount > 0 && (
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white px-1.5 py-0.5 rounded-sm">
                            +{moreCount} weitere
                        </span>
                    )}
                </div>
                <Link
                    to={first.cta_url}
                    className="hidden sm:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-red-900 hover:text-red-700"
                    data-testid="action-banner-cta"
                >
                    {first.cta_label}
                    <ChevronRight className="w-4 h-4" />
                </Link>
                <Link
                    to="/dashboard?tab=inbox"
                    className="text-[10px] font-bold uppercase tracking-wider text-red-700 underline shrink-0"
                    data-testid="action-banner-inbox"
                >
                    Alle ansehen
                </Link>
                <button
                    type="button"
                    onClick={onDismiss}
                    className="p-1 text-red-600 hover:text-red-800 shrink-0"
                    aria-label="Banner schließen"
                    data-testid="action-banner-dismiss"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
